import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { EmptyState } from "../components/EmptyState";
import { Select } from "../components/Select";
import { CollapsePanel, fadeUp, pop, tap } from "../motion";
import { IconChevronLeft, IconChevronRight, IconRota } from "../components/icons";
import { RotaImportWizard } from "../components/RotaImportWizard";
import { RotaDayMenu } from "../components/RotaDayMenu";
import { TimeSelect } from "../components/TimeSelect";
import { useHomes } from "../hooks/useSettings";
import { shiftMonth, useRota } from "../hooks/useRota";
import { hkToday } from "../lib/dates";

const pad = (n: number) => String(n).padStart(2, "0");
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function buildWeeks(month: string): ({ date: string; day: number } | null)[][] {
  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y!, m!, 0).getDate();
  const first = new Date(y!, m! - 1, 1).getDay();
  const cells: ({ date: string; day: number } | null)[] = [];
  for (let i = 0; i < first; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push({ date: `${month}-${pad(d)}`, day: d });
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: ({ date: string; day: number } | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export function RotaPage() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [menuDate, setMenuDate] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [params, setParams] = useSearchParams();
  const dayParam = params.get("day");
  const today = hkToday();
  const monthLabel = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "Asia/Hong_Kong" }).format(new Date(`${month}-01T00:00:00+08:00`));
  const { list, summary, imports, dryRun, commit, createShift, updateShift, deleteShift } = useRota(month);
  const { data: homes } = useHomes();

  const days = list.data?.days ?? [];
  const byDate = new Map(days.map((d) => [d.date, d]));
  const homeName = (id: string) => homes?.find((h) => h.id === id)?.name;
  const homeIndex = (id: string) => homes?.findIndex((h) => h.id === id) ?? -1;
  const homeChipClass = (id: string) => (homes ? `chip rota-home-${Math.abs(homeIndex(id)) % 6} chip-home` : "chip chip-accent");
  const selectedShift = selected ? byDate.get(selected) : undefined;

  const [editHome, setEditHome] = useState("");
  const [editStart, setEditStart] = useState("08:00");
  const [editEnd, setEditEnd] = useState("18:00");
  const [editMarker, setEditMarker] = useState("買位");

  function openDayMenu(date: string, e: React.MouseEvent<HTMLButtonElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    setMenuAnchor({ left: r.left, top: r.top, width: r.width, height: r.height });
    setMenuDate(date);
  }

  function openDay(date: string) {
    setSelected(date);
    const s = byDate.get(date);
    setEditHome(s?.homeId ?? homes?.[0]?.id ?? "");
    setEditStart(s?.startTime ?? "08:00");
    setEditEnd(s?.endTime ?? "18:00");
    setEditMarker(s?.marker ?? "買位");
  }

  // Deep link from global search: /rota?day=YYYY-MM-DD selects that day.
  useEffect(() => {
    if (dayParam && /^\d{4}-\d{2}-\d{2}$/.test(dayParam)) {
      setMonth(dayParam.slice(0, 7));
      openDay(dayParam);
      setParams({}, { replace: true });
    }
  }, [dayParam]);

  // Once a selected day's data arrives, sync the editor fields.
  useEffect(() => {
    if (!selected || !byDate.has(selected)) return;
    const s = byDate.get(selected)!;
    setEditHome(s.homeId);
    setEditStart(s.startTime);
    setEditEnd(s.endTime);
    setEditMarker(s.marker ?? "買位");
  }, [list.data, selected]);

  // After the expand settles, smooth-scroll to the bottom of the page. The
  // page scrolls inside the app shell's content column, not the window, so
  // walk up from the panel to the ancestor that actually scrolls.
  function scrollToEditCard(panel: HTMLElement | null) {
    let el = panel?.parentElement ?? null;
    while (el) {
      const { overflowY } = getComputedStyle(el);
      if (overflowY === "auto" || overflowY === "scroll") {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        return;
      }
      el = el.parentElement;
    }
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  }

  function saveShift() {
    if (!selected || !editHome) return;
    const payload = { date: selected, homeId: editHome, startTime: editStart, endTime: editEnd, marker: editMarker };
    if (selectedShift) updateShift.mutate({ id: selectedShift.id, startTime: editStart, endTime: editEnd, marker: editMarker });
    else createShift.mutate(payload);
  }

  return (
    <>
      <motion.div className="page-head" variants={fadeUp} initial="hidden" animate="show">
        <h1>Rota</h1>
      </motion.div>

      <motion.div className="rota-card" variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.05 }}>
        <div className="toolbar rota-toolbar">
          <button type="button" className="btn btn-secondary btn-icon" onClick={() => setMonth(shiftMonth(new Date(month + "-01T00:00:00+08:00"), -1))} aria-label="Previous month"><IconChevronLeft /></button>
          <span className="log-date rota-month">{monthLabel}</span>
          <button type="button" className="btn btn-secondary" onClick={() => setMonth(today.slice(0, 7))} disabled={month === today.slice(0, 7)}>Today</button>
          <button type="button" className="btn btn-secondary btn-icon" onClick={() => setMonth(shiftMonth(new Date(month + "-01T00:00:00+08:00"), 1))} aria-label="Next month"><IconChevronRight /></button>
          <span className="spacer" />
          <motion.button type="button" className="btn btn-primary" {...tap} onClick={() => setWizardOpen((o) => !o)}>
            {wizardOpen ? "Close import" : "Import PDF"}
          </motion.button>
        </div>

        <AnimatePresence initial={false}>
          {wizardOpen ? <RotaImportWizard dryRun={dryRun} commit={commit} onDone={() => setWizardOpen(false)} /> : null}
        </AnimatePresence>

        {list.isLoading ? <p className="muted rota-loading">Loading…</p> : null}
        {!list.isLoading && days.length === 0 ? (
          <motion.div variants={pop} initial="hidden" animate="show" className="rota-empty">
            <EmptyState
              icon={<IconRota />}
              title="No shifts yet"
            />
          </motion.div>
        ) : null}

        {!list.isLoading ? (
        <div className="rota-grid" role="grid" aria-label={month}>
          {WEEKDAYS.map((w) => (
            <div key={w} className="rota-head">{w}</div>
          ))}
          {buildWeeks(month).flat().map((cell, i) =>
            cell ? (
              <button
                key={cell.date}
                type="button"
                className={`rota-cell ${selected === cell.date || menuDate === cell.date ? "selected" : ""} ${cell.date === today ? "today" : ""} ${byDate.has(cell.date) ? "" : "empty"}`}
                aria-haspopup="menu"
                aria-expanded={menuDate === cell.date}
                onClick={(e) => openDayMenu(cell.date, e)}
              >
                <span className="rota-day-num">{cell.day}</span>
                {byDate.has(cell.date) ? (
                  <span className={`rota-shift rota-home-${Math.abs(homeIndex(byDate.get(cell.date)!.homeId)) % 6}`}>
                    <span className="rota-shift-main">
                      <span className="rota-shift-name">{homeName(byDate.get(cell.date)!.homeId)}</span>
                      <span className="rota-time">{byDate.get(cell.date)!.startTime.slice(0, 5)}–{byDate.get(cell.date)!.endTime.slice(0, 5)}</span>
                    </span>
                  </span>
                ) : (
                  <span className="rota-rest">—</span>
                )}
              </button>
            ) : (
              <div key={`pad-${i}`} className="rota-cell pad" />
            ),
          )}
        </div>
        ) : null}
      </motion.div>

      {summary.data ? (
        <motion.div className="todo-meta" style={{ marginTop: "var(--space-4)" }} variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.08 }}>
          {summary.data.perHome.map((h) => (
            <span key={h.homeId} className={homeChipClass(h.homeId)}>{h.name} · {h.days}d</span>
          ))}
          <span className="chip">rest · {summary.data.restDays.length}d</span>
        </motion.div>
      ) : null}

      <AnimatePresence initial={false}>
      {selected ? (
        <CollapsePanel onShowComplete={scrollToEditCard}>
        <div className="form-card rota-edit">
          <div className="form-row">
            <div>
              <label htmlFor="shift-home">Home</label>
              <Select
                id="shift-home"
                value={editHome}
                onChange={setEditHome}
                options={(homes ?? []).map((h) => ({ value: h.id, label: h.name }))}
              />
            </div>
            <div>
              <label htmlFor="shift-marker">Marker</label>
              <Select
                id="shift-marker"
                value={editMarker}
                onChange={setEditMarker}
                options={[
                  { value: "買位", label: "買位" },
                  { value: "DS", label: "DS" },
                  { value: "院舍券", label: "院舍券" },
                ]}
              />
            </div>
          </div>
          <div className="form-row">
            <div>
              <label htmlFor="shift-start">Start</label>
              <TimeSelect id="shift-start" ariaLabel="Start time" value={editStart} onChange={setEditStart} minuteStep={30} />
            </div>
            <div>
              <label htmlFor="shift-end">End</label>
              <TimeSelect id="shift-end" ariaLabel="End time" value={editEnd} onChange={setEditEnd} minuteStep={30} />
            </div>
          </div>
          <div className="form-actions">
            <motion.button type="button" className="btn btn-primary" {...tap} onClick={saveShift}>
              {selectedShift ? "Save changes" : "Add shift"}
            </motion.button>
            {selectedShift ? (
              <button type="button" className="btn btn-secondary" onClick={() => deleteShift.mutate(selectedShift.id)}>Delete</button>
            ) : null}
            <button type="button" className="btn btn-ghost" onClick={() => setSelected(null)}>Close</button>
          </div>
        </div>
        </CollapsePanel>
      ) : null}
      </AnimatePresence>

      {imports.data && imports.data.length > 0 ? (
        <p className="muted">Last import: {imports.data[0]!.fileName} ({imports.data[0]!.importedAt.slice(0, 10)})</p>
      ) : null}

      <RotaDayMenu
        date={menuDate}
        anchor={menuAnchor}
        onEdit={(d) => {
          openDay(d);
          setMenuDate(null);
          setMenuAnchor(null);
        }}
        onClose={() => {
          setMenuDate(null);
          setMenuAnchor(null);
        }}
      />
    </>
  );
}
